import invariant from 'invariant'

import {batchAnimationFrame, batchDefer, batchIdle} from './batch'

type Global = NodeJS.Global & {
  requestAnimationFrame: Window['requestAnimationFrame'] | undefined
  cancelAnimationFrame: Window['cancelAnimationFrame'] | undefined
  requestIdleCallback: (cb: Function) => any | undefined
  cancelIdleCallback: (id: any) => void | undefined
  performance: Window['performance'] | undefined
  uptime: Function | undefined
  // requestAnimationFrame: Window['requestAnimationFrame'] | undefined,
}
const glbl: Global = typeof window === 'object' ? window : (global as any)

const requestAnimationFrame =
  glbl.requestAnimationFrame ||
  // @ts-expect-error it's fine
  ((fn) => glbl.setTimeout(fn, 0))
// @ts-expect-error it's fine
const cancelAnimationFrame = glbl.cancelAnimationFrame || glbl.clearTimeout
export const uptime: () => number =
  // @ts-ignore
  (glbl.performance && (() => glbl.performance.now())) ||
  (glbl.uptime && (() => process.uptime())) ||
  (() => Date.now())

export type Callback<In extends any[] = any, Out = any> = (...args: In) => Out

type Options = {
  // fire on leading edge of the timeout
  leading?: boolean
  // fire on trailing edge of the timeout
  trailing?: boolean
  // fire if maxWait ms passed since last call
  maxWait?: number
  // allow to call synchronously on leading edge or when maxWait has passed. Otherwise all calls are deferred
  allowSync?: boolean
}

/**
 * Later allows to easily debounce, delay or cancel callbacks.
 *
 * You use it by wrapping a callback with `later()` - this creates a Later object, which you should
 * store somewhere - for example on React component's instance.
 *
 * Now you can invoke this callback asynchronously by calling `delay`, `debounce`, `animationFrame`, `idle` and similar.
 * Every such call *cancels* the previous invocation, meaning, you can for example change the debounce timeout,
 * or use `debounce` on some events and `now` on others, when you need immediate action.
 *
 * When you don't want the callback to be called anymore, for example in `componentWillUnmount`, you can call `destroy()`
 * to make sure it will never be called again.
 *
 * @example
 * let callbackLater = later(callback) // create
 * ...
 * callbackLater.delay(50) // delay by 50ms
 * ...
 * callbackLater.delayMax(50, 150) // delay by 50ms, or call earlier if wait exceeds 150ms
 * ...
 * callbackLater.waitFor(fetch(...)) // delay callback until fetch returns
 * ...
 * callbackLater.setArgs('foo') // change next callback arguments
 * ...
 * await callbackLater // wait for the next callback results with a Promise API
 * ...
 * callbackLater.cancel() // cancel unfired callback
 *
 * */
export class Later<In extends any[], Out> {
  _args: In | []
  _defaultArgs: In | []
  _firstPending: false | number
  _lastFired: false | number
  _deferred: any
  _callback: Callback<In, Out> | null
  _cancel: (() => void) | null
  lastResult: Out | void

  constructor(callback: Callback<In, Out>, ...args: In | []) {
    this.lastResult = undefined

    this._callback = callback || (() => {})
    this._args = args
    this._defaultArgs = args.slice() as any
    this._cancel = null
    this._firstPending = false
    this._lastFired = false
    this._deferred = null
    this.flush = this.flush.bind(this)
    this.cancel = this.cancel.bind(this)
    this.destroy = this.destroy.bind(this)
  }

  /** Set arguments for the callback */
  setArgs(...args: In): Later<In, Out> {
    this._args = args.slice() as any
    return this
  }

  set(...args: In): Later<In, Out> {
    return this.setArgs(...args)
  }

  /** Resets arguments to default ones */
  resetArgs(): Later<In, Out> {
    this._args = this._defaultArgs.slice() as any
    return this
  }

  /** Push additional arguments for the callback */
  pushArgs(...args: In): Later<In, Out> {
    // @ts-ignore
    this._args.push(...args)
    return this
  }

  push(...args: In): Later<In, Out> {
    return this.pushArgs(...args)
  }

  /** Returnes arguments scheduled for next fire */
  getArgs(): In {
    return this._args as any
  }

  /** Cancels pending callback, resets arguments list to default, timers */
  reset(): Later<In, Out> {
    this.cancel()
    this.resetArgs()
    this._lastFired = false
    this.lastResult = undefined
    return this
  }

  /** Invoke callback now */
  now(): Later<In, Out> {
    this.arm(() => {})
    return this.flush()
  }

  defer(): Later<In, Out> {
    return this.delay(0)
  }

  animationFrame(): Later<In, Out> {
    const id: any = requestAnimationFrame(this.flush)
    this.arm(() => cancelAnimationFrame(id))
    return this
  }

  idle(): Later<In, Out> {
    if (!glbl.requestIdleCallback) {
      return this.animationFrame()
    }
    const id = glbl.requestIdleCallback(this.flush)
    this.arm(() => glbl.cancelIdleCallback(id))
    return this
  }

  /* Batches calls together  */
  batchDefer(): Later<In, Out> {
    return this.arm(batchDefer(this.flush))
  }

  batchAnimationFrame(): Later<In, Out> {
    return this.arm(batchAnimationFrame(this.flush))
  }

  batchIdle(): Later<In, Out> {
    return this.arm(batchIdle(this.flush))
  }

  /* Delay call */
  delay(ms: number): Later<In, Out> {
    const id = setTimeout(this.flush, ms)
    this.arm(() => clearTimeout(id))
    return this
  }

  interval(ms: number): Later<In, Out> {
    let l
    const setup = () => {
      this.flush()
      this.arm(() => l.cancel())
      l.delay(ms)
    }
    l = new Later(() => {
      this.flush()
      setup()
    })
    setup()
    return this
  }

  /** Delay call, but not more than max ms */
  delayMax(ms: number, max: number): Later<In, Out> {
    return this.debounce(ms, {
      leading: false,
      trailing: true,
      maxWait: max,
      allowSync: false,
    })
  }

  /** Debounces calls by `wait` */
  debounce(wait: number, options: Options = {}): Later<In, Out> {
    const {
      leading = false,
      trailing = true,
      maxWait,
      allowSync = false,
    } = options

    const sinceLastFired = this.sinceLastFired()
    if (leading) {
      if (sinceLastFired === false || sinceLastFired > wait) {
        // fire on leading edge only on first call or if `wait` has already passed
        return allowSync ? this.now() : this.defer()
      }
    }
    if (maxWait !== undefined) {
      const sinceFirstPending = this.sinceFirstPending()
      const waited =
        sinceFirstPending === false ? sinceLastFired || 0 : sinceFirstPending
      if (maxWait === 0 || waited > maxWait) {
        // fire immediately if more than maxWait has passed or maxWait is 0
        return allowSync ? this.now() : this.defer()
      }
      if (trailing) {
        // if trailing is true, delay until next wait, or maxWait, whichever comes first
        return this.delay(Math.min(wait, maxWait - waited))
      }
      // if trailing is false, delay until maxWait only
      return this.delay(maxWait - waited)
    }
    if (trailing) {
      return this.delay(wait)
    }
    return this
  }

  /** Fire not more often than `ms` */
  throttle(ms: number, options: Options = {}): Later<In, Out> {
    const {leading = true, trailing = true, allowSync = false} = options
    return this.debounce(ms, {
      leading,
      trailing,
      allowSync,
      maxWait: ms,
    })
  }

  /** Like throttle, but does not fire on leading edge */
  throttleTrailing(ms: number): Later<In, Out> {
    if (!ms) return this.now()
    return this.delayMax(ms, ms)
  }

  waitFor(promise: Promise<any>): Later<In, Out> {
    let cancelled = false
    promise.then(() => {
      if (!cancelled) {
        this.flush()
      }
    })
    return this.arm(() => {
      cancelled = true
    })
  }

  isDestroyed() {
    return !this._callback
  }

  isPending() {
    return !!this._cancel
  }

  hasFired() {
    if (typeof this._lastFired !== 'boolean') {
      return this._lastFired > 0
    }
    return false
  }

  /** Returns ms since last fired callback */
  sinceLastFired() {
    if (typeof this._lastFired === 'number') {
      return uptime() - Number(this._lastFired)
    }
    return false
  }

  sinceFirstPending() {
    if (typeof this._firstPending === 'number') {
      return uptime() - Number(this._firstPending)
    }
    return false
  }

  /** Arm callback to be flushed. You have to pass onCancel
   * function which will be called upon cancellation. */
  arm(onCancel: Callback): Later<In, Out> {
    if (this._cancel) {
      this._cancel()
    }
    this._cancel = onCancel
    if (this._firstPending === false) this._firstPending = uptime()
    return this
  }

  /** Cancel currently awaiting callback, if any */
  cancel(): Later<In, Out> {
    if (this._cancel) {
      this._cancel()
    }
    this._cancel = null
    this._firstPending = false
    return this
  }

  /** Cancel currently awaiting callback and never call it again */
  destroy() {
    this.cancel()
    this._callback = null
    this._args = []
    this._defaultArgs = []
  }

  /** Invoke currently awaiting callback now, if any */
  flush(): Later<In, Out> {
    if (this._firstPending !== false) this.fire()
    return this
  }

  /* Invoke callback no matter what. Resets pending arguments */
  fire(): Later<In, Out> {
    this._cancel = null
    if (this._callback) {
      this._firstPending = false
      this._lastFired = uptime()
      const args = this._args
      this.resetArgs()
      // @ts-ignore
      this.lastResult = this._callback(...args)
      if (this._deferred) {
        this._deferred.resolve(this.lastResult)
        this._deferred = null
      }
    }
    return this
  }

  getPromise(): Promise<Out> {
    if (!this._deferred) {
      const promise = new Promise((resolve, reject) => {
        this._deferred = {resolve, reject}
      })
      this._deferred.promise = promise
    }
    return this._deferred.promise
  }

  get promise() {
    return this.getPromise()
  }

  then(onNext?: (v: Out) => any | null, onError?: (e: any) => any | null) {
    return this.getPromise().then(onNext, onError)
  }

  // Aborts this later object by rejecting the promise
  reject(err: Error) {
    this.cancel()
    if (this._deferred) {
      this._deferred.reject(err)
      this._deferred = null
    }
    return this
  }
}

export default function later<In extends any[], Out = any>(
  callback?: Callback<In, Out> | null,
  ...args: In | []
): Later<In, Out> {
  if (callback) {
    return new Later(callback, ...args)
  }
  // @ts-ignore
  return new Later(() => {})
}

function callbackInvoker(...callbacks: Array<Callback>) {
  return callbacks.map((cb) => cb())
}

/** You can use .set() and .push() to change / queue callbacks */
later.callbacks = function laterCallbacks(...callbacks) {
  return later(callbackInvoker, ...callbacks)
}

type WrapableMethods =
  | 'now'
  | 'defer'
  | 'delay'
  | 'delayMax'
  | 'debounce'
  | 'throttle'
  | 'throttleTrailing'
  | 'idle'
  | 'animationFrame'
  | 'batchIdle'
  | 'batchDefer'
  | 'batchAnimationFrame'

/** Wraps provided function in a later callback, making it call laterMethod on every invocation.
 *
 * This provides the API similar to lodash's _.throttle or _.debounce
 *
 * All methods are also available directly, like `throttled = later.throttle(func, 15)`
 *
 * @example
 * func = later.wrap(func, 'throttleTrailing', 10)
 * func('foo') // throttled
 * func('bar') // throttled
 * // after 10ms original func will be called with the last call's arguments - 'bar'
 * func.destroy() // destroys the later object, all subsequent calls will be ignored
 *
 * */
later.wrap = <In extends any[], Out, Method extends WrapableMethods>(
  func: Callback<In, Out>,
  laterMethod: Method,
  ...laterArgs: Parameters<Later<In, Out>[Method]>
) => {
  const funcLater = later<In, Out>(func)
  invariant(funcLater[laterMethod], '%s is not supported', laterMethod)
  const result = (...args: In): Out => {
    funcLater.setArgs(...args)
    // @ts-ignore
    funcLater[laterMethod](...laterArgs)
    return funcLater.lastResult as any
  }
  result.flush = funcLater.flush
  result.cancel = funcLater.cancel
  result.destroy = funcLater.destroy
  return result
}

later.now = <In extends any[], Out>(func: Callback<In, Out>) =>
  later.wrap(func, 'now')

later.defer = <In extends any[], Out>(func: Callback<In, Out>) =>
  later.wrap(func, 'defer')

later.delay = <In extends any[], Out>(func: Callback<In, Out>, ms: number) =>
  later.wrap(func, 'delay', ms)

later.delayMax = <In extends any[], Out>(
  func: Callback<In, Out>,
  ms: number,
  max: number
) => later.wrap(func, 'delayMax', ms, max)

later.debounce = <In extends any[], Out>(
  func: Callback<In, Out>,
  wait: number,
  options: Options = {}
) => later.wrap(func, 'debounce', wait, options)

later.throttle = <In extends any[], Out>(
  func: Callback<In, Out>,
  ms: number,
  options: Options = {}
) => later.wrap(func, 'throttle', ms, options)

later.throttleTrailing = <In extends any[], Out>(
  func: Callback<In, Out>,
  ms: number
) => later.wrap(func, 'throttleTrailing', ms)

later.idle = <In extends any[], Out>(func: Callback<In, Out>) =>
  later.wrap(func, 'idle')

later.animationFrame = <In extends any[], Out>(func: Callback<In, Out>) =>
  later.wrap(func, 'animationFrame')

later.batchIdle = <In extends any[], Out>(func: Callback<In, Out>) =>
  later.wrap(func, 'batchIdle')

later.batchDefer = <In extends any[], Out>(func: Callback<In, Out>) =>
  later.wrap(func, 'batchDefer')

later.batchAnimationFrame = <In extends any[], Out>(func: Callback<In, Out>) =>
  later.wrap(func, 'batchAnimationFrame')

export {later}
